/** The per-firefighter exposure table.
 *
 * Extracted from IncidentDetailPage so the Exposure TAB and the exposure
 * REPORT render through one component: they show the same numbers to the same
 * chief, and two renderers of one dataset is how they drift apart (the report
 * had peaks only while the tab had dose/TWA/confidence/status — 2026-07-30).
 */
import { Badge } from "@/components/ui/badge";
import { AlertBadge } from "@/components/alert-badge";
import { formatPpm, toNum as toNumber } from "@/lib/format";
import { formatSeconds } from "@/lib/reports";
import {
  CO_NIOSH_REL_PPM,
  VOC_SCREEN_PPM,
  exposureStatus,
} from "@/lib/carcinogen";
import type { components } from "@/api/types";

export type ExposureRow = components["schemas"]["ExposureRecordRead"];

/**
 * Cumulative dose in ppm·min (%LEL·min for LEL) — the real carcinogen metric
 * (∫C·dt), integrated from the firefighter's readings at incident close. "—" when
 * the dose columns are null (rows written before the dose engine / no integrable
 * samples), so the table degrades to the peak screen rather than inventing a 0.
 */
function formatDose(v: string | number | null | undefined): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return n >= 100 ? n.toFixed(0) : n.toFixed(1);
}

/**
 * Per-firefighter exposure for the incident. Surfaces what the record actually
 * carries — time-in-zone, gas dose/peaks, and the carcinogen + acute screens built
 * from the channels the BW Ultra really measures: TOTAL VOC (the unspeciated
 * aromatic load) as the carcinogen indicator, and CO / H₂S / LEL as acute hazards
 * — instead of the always-null benzene/PAH columns the device can't populate
 * (see src/lib/carcinogen.ts). We never claim a speciated benzene measurement.
 */
export function ExposureTable({
  records,
}: {
  records: ExposureRow[];
}) {
  return (
    // exposure-block / exposure-note / exposure-scroll are print hooks: on
    // paper the scope note moves BELOW the table it qualifies (a footnote a
    // reader meets before the numbers is one they read twice or not at all),
    // and the horizontal scroller stops clipping. Screen layout is unchanged.
    // See docs/design/report-print-design-language.md.
    <div className="exposure-block space-y-3">
      <p className="exposure-note text-xs text-muted-foreground">
        Per-firefighter exposure aggregated from this incident's sensor readings.
        The <strong>carcinogen</strong> metric is the cumulative{" "}
        <strong>total-VOC dose</strong> (ppm·min, ∫C·dt). Total VOC is the
        unspeciated aromatic load, used directly as the carcinogen indicator. A
        VOC peak ≥ {VOC_SCREEN_PPM} ppm screens it as elevated.{" "}
        <strong>CO is an acute hazard</strong>: a measured CO TWA ≥{" "}
        {CO_NIOSH_REL_PPM} ppm is over the NIOSH REL. Status flags the carcinogen
        signal (total VOC) and the acute limits (CO REL, H₂S ceiling, LEL action
        level). Dose/TWA show "—" on incidents closed before the dose engine (the
        screen still applies).
      </p>
      <div className="exposure-scroll overflow-x-auto">
        <table className="data-table w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-3">Firefighter</th>
              <th className="py-1 pr-3">Sensor time</th>
              <th className="py-1 pr-3">CO dose (ppm·min)</th>
              <th className="py-1 pr-3">CO TWA</th>
              <th className="py-1 pr-3">VOC dose (ppm·min)</th>
              <th className="py-1 pr-3">CO peak</th>
              <th className="py-1 pr-3">VOC peak</th>
              <th className="py-1 pr-3">H₂S peak</th>
              <th className="py-1 pr-3">LEL peak</th>
              <th className="py-1 pr-3">Confidence</th>
              <th className="py-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => {
              const status = exposureStatus(r);
              const lel = toNumber(r.lel_pct_peak);
              // Server-resolved identity only (RES-165): the API applies the
              // display rule (full_name, else email, else null) and includes
              // members who have left the department. `||`, not `??`, because
              // full_name is NOT NULL but CAN be empty, and an empty string
              // through `??` produced a completely blank Firefighter cell —
              // which reads as "nobody" on an exposure record. The UUID stub
              // is the last resort for a hard-deleted user, kept visible, with
              // the full id on hover ONLY there — well-named rows must not
              // leak raw identifiers into every chief's hover and screen
              // recording.
              const name = r.user_name || null;
              const label =
                name && r.user_badge_number ? `${name} · ${r.user_badge_number}` : name;
              return (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="py-1 pr-3">
                    {label ?? (
                      <span className="font-mono text-xs" title={r.user_id}>
                        {r.user_id.slice(0, 8)}
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-3 text-xs tabular-nums">
                    {formatSeconds(r.interior_seconds)}
                  </td>
                  <td className="py-1 pr-3 font-medium tabular-nums">
                    {formatDose(r.co_ppm_min)}
                  </td>
                  <td className="py-1 pr-3 tabular-nums">
                    {formatPpm(toNumber(r.co_twa_ppm))}
                  </td>
                  <td className="py-1 pr-3 font-medium tabular-nums">
                    {formatDose(r.voc_ppm_min)}
                  </td>
                  <td className="py-1 pr-3 text-xs tabular-nums text-muted-foreground">
                    {formatPpm(toNumber(r.co_ppm_peak))}
                  </td>
                  <td className="py-1 pr-3 text-xs tabular-nums text-muted-foreground">
                    {formatPpm(toNumber(r.voc_ppm_peak))}
                  </td>
                  <td className="py-1 pr-3 tabular-nums">
                    {formatPpm(toNumber(r.h2s_ppm_peak))}
                  </td>
                  <td className="py-1 pr-3 tabular-nums">
                    {lel === null ? "—" : `${lel.toFixed(1)}%`}
                  </td>
                  <td className="py-1 pr-3 text-xs capitalize">
                    {r.confidence}
                  </td>
                  <td className="py-1">
                    {status.over ? (
                      <div
                        className="flex flex-wrap items-center gap-1"
                        title={status.reasons.join("; ")}
                      >
                        {status.carcinogenOver && (
                          <Badge variant="destructive">Carcinogen</Badge>
                        )}
                        {status.acuteOver && (
                          <Badge variant="warning">Acute</Badge>
                        )}
                      </div>
                    ) : (
                      <AlertBadge level="none" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
